"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { removeEventFromPatientStatusCache } from "@/hooks/use-checklist-realtime-cache";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import type { AlertDetail, AppointmentChecklistUpdatedData } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import { AppointmentChecklistType } from "@/types/enums/appointment";
import { EventIcon, EventStatus } from "@/types/enums/event";
import { EventTiny, UpdatedAppointment } from "@/types/event";
import { eventLogQueryOptions } from "@/query/check-in/calendar/event-log-query";
import { addEventToCalendarCache, eventQueryKeys, removeEventFromCalendarCaches, updateEventIconsInCalendarCaches, updateEventInCalendarCaches, updateEventStatusInCalendarCaches } from "@/query/check-in/calendar/event-query";

/**
 * Mount once, console-wide. Cached ranges must stay current while the calendar
 * is off screen, since returning to it within staleTime serves the cache
 * without refetching.
 */
export function useEventRangeRealtimeSync() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = () => {
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.byLocations()
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const refetch = t0;
  let t1;
  if ($[2] !== queryClient) {
    t1 = function handleMessage(message) {
      bb3: switch (message.type) {
        case AlertType.APPOINTMENT_SCHEDULED:
        case AlertType.SELF_SCHEDULED_APPOINTMENT:
        case AlertType.BLOCK_CREATED:
        case AlertType.SHIFT_CREATED:
        case AlertType.GCAL_EVENT_CREATED:
          {
            const newEvent = message.data as EventTiny;
            addEventToCalendarCache(newEvent);
            break bb3;
          }
        case AlertType.APPOINTMENT_UPDATED:
        case AlertType.SELF_SCHEDULED_APPOINTMENT_UPDATED:
          {
            const updateData = message.data as UpdatedAppointment;
            const updatedEvent_0 = updateData.updatedTo;
            updateEventInCalendarCaches(updatedEvent_0);
            if (updatedEvent_0.status === EventStatus.COMPLETED) {
              removeEventFromPatientStatusCache(updatedEvent_0.id);
            }
            queryClient.invalidateQueries({
              queryKey: eventQueryKeys.detail(updatedEvent_0.id)
            });
            queryClient.invalidateQueries({
              queryKey: eventQueryKeys.appointmentChecklist(updatedEvent_0.id)
            });
            queryClient.invalidateQueries(eventLogQueryOptions.list(updatedEvent_0.id));
            break bb3;
          }
        case AlertType.BLOCK_UPDATED:
        case AlertType.SHIFT_UPDATED:
        case AlertType.GCAL_EVENT_UPDATED:
          {
            const updatedEvent = message.data as EventTiny;
            updateEventInCalendarCaches(updatedEvent);
            break bb3;
          }
        case AlertType.APPOINTMENT_CANCELLED:
        case AlertType.SELF_SCHEDULED_APPOINTMENT_CANCELLED:
        case AlertType.BLOCK_CANCELLED:
        case AlertType.SHIFT_CANCELLED:
        case AlertType.GCAL_EVENT_CANCELLED:
          {
            const cancelledEvent = message.data as EventTiny;
            const isAppointmentCancelled = message.type === AlertType.APPOINTMENT_CANCELLED || message.type === AlertType.SELF_SCHEDULED_APPOINTMENT_CANCELLED;
            if (isAppointmentCancelled) {
              updateEventStatusInCalendarCaches(cancelledEvent.id, EventStatus.CANCELLED);
              removeEventFromPatientStatusCache(cancelledEvent.id);
            } else {
              removeEventFromCalendarCaches(cancelledEvent.id);
            }
            queryClient.invalidateQueries({
              queryKey: eventQueryKeys.detail(cancelledEvent.id)
            });
            queryClient.invalidateQueries(eventLogQueryOptions.list(cancelledEvent.id));
            break bb3;
          }
        case AlertType.APPOINTMENT_CHECKLIST_CHECKED_IN:
        case AlertType.APPOINTMENT_CHECKLIST_NOTE_TAKEN:
        case AlertType.APPOINTMENT_CHECKLIST_FILLED_FORM:
          {
            const alertData = message.data as AppointmentChecklistUpdatedData;
            const eventId = alertData.eventId;
            const checklistType = alertData.appointmentChecklistItem.type;
            const shouldAddIcon = !!alertData.appointmentChecklistItem.completedDate;
            if (checklistType === AppointmentChecklistType.CHECKED_IN) {
              updateEventIconsInCalendarCaches(eventId, prevIcons => shouldAddIcon ? Array.from(new Set([...prevIcons, EventIcon.CHECKED_IN])) : prevIcons.filter(_temp));
            } else {
              if (checklistType === AppointmentChecklistType.NOTE_TAKEN) {
                updateEventIconsInCalendarCaches(eventId, prevIcons_0 => shouldAddIcon && alertData.noteLinked ? Array.from(new Set([...prevIcons_0, EventIcon.NOTE_LINKED])) : prevIcons_0.filter(_temp2));
              } else {
                if (checklistType === AppointmentChecklistType.FILLED_FORM && shouldAddIcon) {
                  updateEventIconsInCalendarCaches(eventId, _temp4);
                }
              }
            }
          }
      }
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.byLocations(),
        refetchType: "none"
      });
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleMessage = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [AlertType.APPOINTMENT_SCHEDULED, AlertType.APPOINTMENT_UPDATED, AlertType.APPOINTMENT_CANCELLED, AlertType.SELF_SCHEDULED_APPOINTMENT, AlertType.SELF_SCHEDULED_APPOINTMENT_UPDATED, AlertType.SELF_SCHEDULED_APPOINTMENT_CANCELLED, AlertType.BLOCK_CREATED, AlertType.BLOCK_UPDATED, AlertType.BLOCK_CANCELLED, AlertType.SHIFT_CREATED, AlertType.SHIFT_UPDATED, AlertType.SHIFT_CANCELLED, AlertType.GCAL_EVENT_CREATED, AlertType.GCAL_EVENT_UPDATED, AlertType.GCAL_EVENT_CANCELLED, AlertType.APPOINTMENT_CHECKLIST_CHECKED_IN, AlertType.APPOINTMENT_CHECKLIST_NOTE_TAKEN, AlertType.APPOINTMENT_CHECKLIST_FILLED_FORM];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    isConnected
  } = useWebSocketCallbackSubscription(t2, handleMessage, true, "eventRangeRealtimeSync");
  const prevIsConnectedRef = useRef(null);
  const hasEverConnectedRef = useRef(false);
  let t3;
  let t4;
  if ($[5] !== isConnected || $[6] !== refetch) {
    t3 = () => {
      const wasDisconnected = prevIsConnectedRef.current === false;
      const isNowConnected = isConnected === true;
      if (wasDisconnected && isNowConnected && hasEverConnectedRef.current) {
        refetch();
      }
      if (isConnected) {
        hasEverConnectedRef.current = true;
      }
      prevIsConnectedRef.current = isConnected;
    };
    t4 = [isConnected, refetch];
    $[5] = isConnected;
    $[6] = refetch;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
}
function _temp4(prevIcons_1) {
  return Array.from(new Set([...prevIcons_1.filter(_temp3), EventIcon.FORM_COMPLETED]));
}
function _temp3(icon_1) {
  return icon_1 !== EventIcon.FORM_NOT_COMPLETED;
}
function _temp2(icon_0) {
  return icon_0 !== EventIcon.NOTE_LINKED;
}
function _temp(icon) {
  return icon !== EventIcon.CHECKED_IN;
}